import {
  Header,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { InspectionValues } from '@parallel-fluidics/shared/constants';
import { travelerToFirestore } from '@parallel-fluidics/shared/models/travelers';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import {
  collection,
  doc,
  getFirestore,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useCallback,
  useState,
} from 'react';
import { toast } from 'sonner';

import AddTravelersTable from './addTravelersTable';

export default function AddTravelers({ lineItemData }) {
  const { user } = useUserData();

  const [visible, setVisible] = useState(false);
  const [scannedTravelers, setScannedTravelers] = useState([]);

  const handleQRCodeSuccess = useCallback((travelerId) => {
    setScannedTravelers((prev) => {
      if (prev.includes(travelerId)) {
        return prev;
      }
      return [...prev, travelerId];
    });
  }, []);

  const handleAddTravelers = useCallback(async (travelerIds) => {
    try {
      const travelerData = {
        lineItem: lineItemData.lineItemId,
        order: lineItemData.lineItemId.split('_')[1],
        pPart: lineItemData.pPartId,
        project: lineItemData.projectId,
        shipped: false,
        steps: lineItemData.steps.map((step) => ({
          type: step.type,
          pPartId: step.pPartId,
          pPartDocId: step.pPartDocId,
          inspected: InspectionValues.NOT_INSPECTED,
          inspectedNote: '',
        })),
        _updatedBy: {
          email: user.email,
          uid: user.id,
        },
      };
      await Promise.all(travelerIds.map(async (travelerId) => {
        const docData = travelerToFirestore(travelerData, serverTimestamp);
        const travelerRef = doc(collection(getFirestore(), 'travelers'), travelerId);
        await setDoc(travelerRef, docData);
      }));
      setScannedTravelers([]);
      setVisible(false);
      toast.success(`Successfully added ${travelerIds.length} travelers!`);
    } catch (error) {
      toast.error(`Error adding travelers: ${error.message}`);
    }
  }, [
    lineItemData.lineItemId,
    lineItemData.pPartId,
    lineItemData.projectId,
    lineItemData.steps,
    user.email,
    user.id,
  ]);

  return (
    <>
      <Modal
        onDismiss={() => setVisible(false)}
        visible={visible}
        header={(
          <Header>
            {`Scan to add travelers for ${lineItemData.projectId}-${lineItemData.pPartId}`}
          </Header>
        )}
      >
        <SpaceBetween size="m">
          {scannedTravelers?.length > 0 && (
            <AddTravelersTable
              scannedTravelers={scannedTravelers}
              onAddTravelers={handleAddTravelers}
            />
          )}
          {visible && (
            <ScanField
              id="add-travelers-scanner"
              accept={[ScanTypes.TRAVELER]}
              onScan={({ id }) => handleQRCodeSuccess(id)}
              cameraMode="inline"
              autoFocus
              allowGenerate
            />
          )}
        </SpaceBetween>
      </Modal>
      {/**
       * Custom CSS is affecting the Cloudscape Button,
       * so using a simple button with inline style override here
       */}
      <button type="button" style={{ border: 0 }} onClick={() => setVisible(true)}>Add Travelers</button>
    </>
  );
}

AddTravelers.propTypes = {
  lineItemData: PropTypes.shape({
    lineItemId: PropTypes.string.isRequired,
    pPartId: PropTypes.string,
    projectId: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        pPartId: PropTypes.string,
        pPartDocId: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
};
